import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { t } from "i18next";
import C from "Constants";
import { UTILS } from "utils";
import { StarRating } from "components";
import ShowDeliveryTime from "components/ShowDeliveryTime";
import { DownArrow, LocationIcon, BasketBag, ClockIcon } from "config/Svgs";
import { WhatsappIcon } from "icons";
import AED from "icons/AED";
import { useLang } from "hooks";
import ConditionalRenderer from "./ConditionalRenderer";
import { PriceRender } from "./DisplayPrice";

//TODO: remove ltr dir from the component, if arabic content support is added for a store from backend in future

const KeyMap = {
  [C.SERVICE_TYPE.DELIVERY]: (selectedArea, selectedStore) => (
    <>
      <ShowDeliveryTime val={selectedArea?.deliveryTime} />
    </>
  ),
  [C.SERVICE_TYPE.PICKUP]: (selectedArea, selectedStore) => (
    <>{selectedStore?.averagePrepTimeForPickup} </>
  ),
  [C.SERVICE_TYPE.DINE_IN]: (selectedArea, selectedStore) => (
    <>{selectedStore?.averagePrepTimeForDineIn} </>
  ),
};

const RenderContentWithIcon = ({
  Icon,
  content,
  className = "items-center",
  contentClassName = "",
}) => {
  return (
    <div
      className={`flex text-13x text-shadow-forge-700 ${className}`}
      style={{
        gap: "6px",
      }}
    >
      <Icon
        width={16}
        height={16}
        className="text-brand-base-primary"
        stroke="currentColor"
      />
      <span className={contentClassName}>{content}</span>
    </div>
  );
};

const StoreHeaderDetail = ({
  page,
  brandInfo,
  description,
  headerTitle,
  containerStyleProps = {},
  titleStyleProps = {},
  descriptionStyleProps = {},
  rattingStyleProps = {},
  onNavigateClick = () => {},
  hideDescription = false,
  hideStoreTitle = false,
  hideStoreRatting = true,
  showManualAddress = false,
  showStoreInfo = false,
  showArrow = true,
  children,
  orderType,
  selectedArea,
}) => {
  const { selectedStore } = useSelector((state) => state?.store);
  const { orderType: serviceType } = useSelector((state) => state.AppReducer);
  const params = useParams();
  const { isArabic } = useLang();

  const isBrandPage = page === "brand";

  const baseData = useMemo(() => {
    const tagLine = isArabic ? "taglineAR" : "taglineEN";
    return {
      headerTitle:
        description === C.SERVICE_TYPE.DINE_IN
          ? headerTitle
          : selectedStore?.baseLanguage?.name ??
            brandInfo?.brand?.brandName ??
            params?.domainName,
      storeDescription: isBrandPage
        ? brandInfo?.setting?.[tagLine]
        : page === "store" ? selectedStore?.baseLanguage?.description : UTILS.getStoreHeadetailDes(description) ?? params?.domainName,
      manualAddress: selectedStore?.manualAddress ?? null,
      whatsAppContact: selectedStore?.whatsAppContact ?? null,
      minOrder: selectedArea?.minOrder ?? null,
    };
  }, [selectedStore, isArabic, brandInfo, selectedArea, description]);

  return (
    <div
      className={`${
        isBrandPage ? "pt-5" : "py-5"
      } px-4 bg-white flex justify-between flex-col gap-2 ${
        containerStyleProps?.className ?? ""
      }`}
      style={containerStyleProps?.style}
    >
      {/* title */}
      <ConditionalRenderer condition={!hideStoreTitle}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h1
              dir={"ltr"}
              className={`font-nunito text-1.5 font-extrabold text-black mb-1 flex items-center gap-2 ${
                titleStyleProps?.className ?? ""
              }`}
              style={titleStyleProps?.style}
            >
              {t(baseData?.headerTitle)}
            </h1>
            {orderType && (
              <span
                className="px-4 py-1 rounded-full text-brand-base-primary text-10x font-semibold"
                style={{ backgroundColor: "var(--light-brand-base-primary)" }}
              >
                {t(orderType)}
              </span>
            )}
            {selectedStore?.whatsAppContact && (
              <a
                href={`https://wa.me/${selectedStore?.whatsAppContact}`}
                className="cursor-pointer"
                target="_blank"
                rel="noopener noreferrer"
              >
                <WhatsappIcon />
              </a>
            )}
          </div>
          {showArrow && (
            <button onClick={onNavigateClick}>
              <DownArrow className="cursor-pointer w-5 h-5 text-shadow-forge-700 rotate-270 hover:text-brand-base-primary hover:scale-125 transition-all duration-300 " />
            </button>
          )}
        </div>
      </ConditionalRenderer>
      {/* store ratting */}
      <ConditionalRenderer condition={!hideStoreRatting}>
        <div
          className={`flex items-center gap-2 text-13x text-shadow-forge-700 font-normal ${
            rattingStyleProps?.className ?? ""
          }`}
          style={rattingStyleProps?.style}
        >
          <>
            <StarRating maxStars={1} rating={3} reviewsCount={10} />
            <span className="w-1 h-1 bg-shadow-forge-700 rounded-full" />
          </>
          {brandInfo?.brand?.catalogueType && (
            <>
              <span title="AED">
                <AED/>
              </span>
              <span className="w-1 h-1 bg-shadow-forge-700 rounded-full" />
              <span>
                {brandInfo?.brand?.catalogueType?.replaceAll("_", " ")}
              </span>
            </>
          )}
        </div>
      </ConditionalRenderer>
      {/* store description */}
      <ConditionalRenderer condition={!hideDescription}>
        <div
          className={`p-3 bg-grey-green-off rounded-16x text-sm leading-normal text-black ${
            descriptionStyleProps?.className ?? ""
          }`}
          style={descriptionStyleProps?.style}
        >
          {t(baseData?.storeDescription)}
        </div>
      </ConditionalRenderer>
      {/* manual address */}
      <ConditionalRenderer condition={showManualAddress}>
        <RenderContentWithIcon
          Icon={LocationIcon}
          content={t(baseData?.manualAddress)}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={showStoreInfo}>
        <div className="flex justify-between items-center">
        {KeyMap?.[serviceType?.EN?.name] && (
            <RenderContentWithIcon
              Icon={ClockIcon}
              content={KeyMap?.[serviceType?.EN?.name](
                selectedArea,
                selectedStore
              )}
            />
          )}
          {+baseData?.minOrder > 0 && (
            <RenderContentWithIcon
              Icon={BasketBag}
              content={
                <div className="flex items-center gap-2">
                  <span className="text-brand-base-primary">
                    {t("minOrder")}
                  </span>
                  <PriceRender price={+baseData?.minOrder} />
                </div>
              }
            />
          )}
        </div>
      </ConditionalRenderer>

      {children && <div className="mt-2">{children}</div>}
    </div>
  );
};

const stylePropsShape = PropTypes.shape({
  className: PropTypes.string,
  style: PropTypes.object,
});

StoreHeaderDetail.propTypes = {
  page: PropTypes.string,
  brandInfo: PropTypes.shape({
    brand: PropTypes.shape({
      brandName: PropTypes.string,
      catalogueType: PropTypes.string,
    }),
  }),
  description: PropTypes.string,
  headerTitle: PropTypes.string,
  containerStyleProps: stylePropsShape,
  titleStyleProps: stylePropsShape,
  descriptionStyleProps: stylePropsShape,
  rattingStyleProps: stylePropsShape,
  hideDescription: PropTypes.bool,
  hideStoreTitle: PropTypes.bool,
  hideStoreRatting: PropTypes.bool,
  showManualAddress: PropTypes.bool,
  showMinRequiredInfo: PropTypes.bool,
  showArrow: PropTypes.bool,
  children: PropTypes.node,
};

export default StoreHeaderDetail;
